// ============================================================================
// Instagram caption formatter (Epic 14 — TOP-2 "Copiar p/ Instagram").
//
// Turns a generated artifact (markdown-ish text produced by the content agents)
// into a clean block a creator can paste straight into Instagram:
//   • strips markdown syntax (##, **, *, _, `, links, bullets/ordered lists);
//   • drops the internal/production "meta" sections that make no sense in a
//     public caption — Objetivo / Avatar utilizado / Estratégia / Orientação de
//     gravação — keeping only the publishable content (gancho, falas, CTA,
//     legenda);
//   • gathers every #hashtag scattered through the text and regroups them,
//     de-duplicated and in first-seen order, at the very end;
//   • preserves paragraph breaks (a single blank line between blocks).
//
// The reels agents emit meta labels in **bold** with an optional number prefix
// and inline value, e.g. `**1. Objetivo**: Atrair...`, `**5. Orientação de
// gravação (LO-FI)**: ...`; publishable labels look the same (`**GANCHO:**`,
// `**CTA:**`). A "meta section" therefore runs from its bold/heading label line
// until the next label line, and is removed whole. Non-meta labels are kept but
// de-marked (so `**GANCHO:**` becomes the plain line `GANCHO:`), which is the
// literal reading of the spec ("## → linha") and leaves the caller in control of
// how much structure to keep. Pure and side-effect free — unit tested.
// ============================================================================

// Normalized label prefixes that mark an internal/production section (accent- and
// case-insensitive). `avatar` uses a word boundary so it won't swallow a real
// line that merely starts with the word.
const META_LABEL_PATTERNS = [/^objetivo/, /^avatar\b/, /^publico[ -]alvo\b/, /^estrateg/, /^orientac/];

// `#tag` token: a hash immediately followed by word chars (incl. accented ones).
// The leading (^|\s) guard keeps it from ever matching a markdown heading, whose
// hash is always followed by a space.
const HASHTAG_RE = /(^|\s)(#[0-9A-Za-zÀ-ÿ_]+)/g;

function stripDiacritics(s) {
  return s.normalize("NFD").replace(/\p{Diacritic}/gu, "");
}

// Reduce a raw label to a comparable token: drop numbering ("1. "), parentheticals
// ("(LO-FI)"), markdown markers and colons, then lowercase without accents.
function normalizeLabel(raw) {
  return stripDiacritics(
    raw
      .replace(/^\s*\d+\s*[.)]\s*/, "")
      .replace(/\(.*?\)/g, "")
      .replace(/[*`_#:]/g, "")
      .trim()
  ).toLowerCase();
}

function isMetaLabel(raw) {
  const n = normalizeLabel(raw);
  return META_LABEL_PATTERNS.some((re) => re.test(n));
}

// The label of a section-header line, or null when the line isn't a header.
// Headers are markdown headings (`## Foo`) or bold-label lines (`**Foo:**` /
// `**1. Foo**: value`). Arbitrary "Word: value" lines are intentionally NOT
// treated as headers, so scene lines like "Cena 1: ..." stay as body.
function sectionHeaderLabel(line) {
  const t = line.trim();
  const heading = t.match(/^#{1,6}\s+(.+)$/);
  if (heading) return heading[1];
  const bold = t.match(/^\*\*\s*(.+?)\s*\*\*\s*:?.*$/);
  if (bold) return bold[1];
  return null;
}

// Linha separadora de tabela GFM: |---|:--:|---| (nada a mostrar na legenda).
const TABLE_DELIM_RE = /^\s*\|?[\s:-]*-[\s:|-]*\|[\s:|-]*$/;
// Linha de tabela GFM: | a | b | c |
const TABLE_ROW_RE = /^\s*\|.*\|\s*$/;

// Tabela não sobrevive ao copiar-e-colar do Instagram: vira "a — b — c".
function flattenTableRow(line) {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim())
    .filter(Boolean)
    .join(" — ");
}

// Remove inline markdown from a single line (headings/bullets/ordered markers,
// bold, italics, code, strikethrough, links, task-list markers).
function stripInlineMarkdown(line) {
  return line
    .replace(/^\s*#{1,6}\s+/, "")
    .replace(/^\s*>\s?/, "")
    .replace(/^\s*[-*+]\s+/, "")
    .replace(/^\s*\d+\s*[.)]\s+/, "")
    // O marcador de checklist sobra depois que o bullet sai: "- [ ] item" → "[ ] item".
    .replace(/^\s*\[([ xX])\]\s+/, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/(^|[^*])\*(?!\*)(.+?)\*(?!\*)/g, "$1$2")
    .replace(/_(.+?)_/g, "$1")
    .replace(/~~(.+?)~~/g, "$1")
    .replace(/`(.+?)`/g, "$1")
    .replace(/\[(.+?)\]\((.+?)\)/g, "$1");
}

function isLegendaHeader(line) {
  const label = sectionHeaderLabel(line);
  if (label !== null) {
    const n = normalizeLabel(label);
    return /^legenda\b/.test(n) || n === "caption";
  }
  return /^legenda\s*:/i.test(stripDiacritics(line.trim()));
}

function inlineAfterLegendaHeader(line) {
  const t = line.trim();
  const heading = t.match(/^#{1,6}\s+(.+)$/);
  if (heading) {
    return heading[1]
      .replace(/^[^A-Za-z0-9]*\s*legenda\b\s*[:—–-]*\s*/i, "")
      .trim();
  }
  const bold = t.match(/^\*\*\s*(.+?)\s*\*\*\s*:?\s*(.*)$/);
  if (bold) return (bold[2] || "").trim();
  const plain = t.match(/^legenda\s*:\s*(.*)$/i);
  if (plain) return (plain[1] || "").trim();
  return "";
}

function extractLegendaSection(markdown) {
  const body = [];
  let collecting = false;
  for (const line of markdown.split(/\r?\n/)) {
    if (isLegendaHeader(line)) {
      collecting = true;
      const rest = inlineAfterLegendaHeader(line);
      if (rest) body.push(rest);
      continue;
    }
    if (!collecting) continue;
    if (sectionHeaderLabel(line) !== null) break;
    body.push(line);
  }
  return body.join("\n").trim();
}

function looksLikeStructuredArtifact(markdown) {
  const lines = markdown.split(/\r?\n/);
  let hasTable = false;
  let hasDelim = false;
  for (const line of lines) {
    if (/^#{1,6}\s*(?:[^A-Za-z0-9\n]*\s*)?card\s*\d+/i.test(line.trim())) return true;
    const label = sectionHeaderLabel(line);
    if (label !== null) {
      const n = normalizeLabel(label);
      if (isMetaLabel(label) || /^roteiro\b/.test(n)) return true;
    }
    if (TABLE_DELIM_RE.test(line)) hasDelim = true;
    if (TABLE_ROW_RE.test(line)) hasTable = true;
  }
  if (hasTable && hasDelim) return true;
  const hasGancho = /\*\*\s*gancho/i.test(markdown) || /^gancho\s*:/im.test(markdown);
  const hasCena = /^cena\s*\d+/im.test(markdown);
  return hasGancho && hasCena;
}

function collectHashtags(markdown) {
  const tags = [];
  const seen = new Set();
  for (const m of markdown.matchAll(HASHTAG_RE)) {
    const tag = m[2];
    const key = tag.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      tags.push(tag);
    }
  }
  return tags;
}

/**
 * Caption ready to paste on Instagram.
 * Prefers a dedicated Legenda section (carrossel / **Legenda:**). Structured
 * artifacts without one (roteiro, cards, calendário) return "" so the toolbar
 * hides the Instagram button instead of copying the whole piece.
 */
export function extractInstagramCaption(markdown) {
  if (!markdown || typeof markdown !== "string") return "";
  const section = extractLegendaSection(markdown);
  if (section) {
    const tags = collectHashtags(markdown);
    const merged = tags.length ? `${section}\n\n${tags.join(" ")}` : section;
    return formatInstagramCaption(merged);
  }
  if (looksLikeStructuredArtifact(markdown)) return "";
  return formatInstagramCaption(markdown);
}

export function hasInstagramCaption(markdown) {
  return Boolean(extractInstagramCaption(markdown));
}

/**
 * Format artifact markdown into a paste-ready Instagram caption.
 * @param {string} markdown raw artifact content
 * @returns {string} cleaned caption with hashtags grouped at the end
 */
export function formatInstagramCaption(markdown) {
  if (!markdown || typeof markdown !== "string") return "";

  // 1) Collect hashtags up front (first-seen order, de-duplicated case-
  //    insensitively) and strip them from the text, so they survive no matter
  //    which section they sat in and always end up in the trailing group.
  const tags = [];
  const seen = new Set();
  for (const m of markdown.matchAll(HASHTAG_RE)) {
    const tag = m[2];
    const key = tag.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      tags.push(tag);
    }
  }
  const noTags = markdown.replace(HASHTAG_RE, "$1");

  // 2) Drop meta sections; de-mark everything else, line by line. A meta section
  //    runs from its label line until the next header OR the next blank line, so
  //    a trailing meta block (e.g. "Orientação de gravação") never swallows the
  //    legenda that follows it.
  const kept = [];
  let skipping = false;
  for (const rawLine of noTags.split(/\r?\n/)) {
    const label = sectionHeaderLabel(rawLine);
    if (label !== null) {
      skipping = isMetaLabel(label);
      if (skipping) continue;
      kept.push(stripInlineMarkdown(rawLine));
      continue;
    }
    if (rawLine.trim() === "") {
      skipping = false;
      kept.push("");
      continue;
    }
    if (skipping) continue;
    // Tabela GFM: o separador some e a linha vira texto legível. Sem isso a
    // legenda ia para o Instagram com os pipes crus (UAT 2026-08-13).
    if (TABLE_DELIM_RE.test(rawLine)) continue;
    if (TABLE_ROW_RE.test(rawLine)) {
      kept.push(stripInlineMarkdown(flattenTableRow(rawLine)));
      continue;
    }
    kept.push(stripInlineMarkdown(rawLine));
  }

  // 3) Tidy whitespace: collapse runs of spaces, trim trailing spaces, and keep
  //    at most one blank line between paragraphs (preserving double breaks).
  let body = kept
    .join("\n")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/[ \t]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  const tagLine = tags.join(" ");
  if (!tagLine) return body;
  return body ? `${body}\n\n${tagLine}` : tagLine;
}

export default formatInstagramCaption;
